/**
 * BoardV3 Floating Toolbar
 * Smart component: accesses stores directly (no prop-drilling)
 *
 * 4-stage height-based resize (like main nav but vertical):
 *   tiny   (14px) – ultra-discreet thin bar, tiny icons
 *   small  (24px) – compact icons
 *   medium (32px) – normal (default)
 *   full   (44px) – spacious, larger icons
 *
 * Resize handle on top edge. Drag up = grow, drag down = shrink.
 * Double-click resize handle = toggle tiny ↔ full.
 */

import { memo, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  ArrowLeftRight,
  Trash2,
  MinusCircle,
  Undo2,
  GripVertical,
  ClipboardList,
  ChevronUp,
  Eye,
} from 'lucide-react';
import { useSelector } from '@xstate/store/react';
import { selectionStore } from '../../stores/selectionStore';
import { popupStore } from '../../stores/popupStore';
import { globalTasksStore } from '../../../stores/globalTasksStore';
import {
  useAllTasks,
  useRoadmapActions,
  useBoardStartDate,
} from '../../../stores/hooks';
import { recalculateSchedule } from '../../../stores';
import { getTaskDayIndex, dateToBusinessDayIndex } from '../../../lib/dateUtils';
import { ShiftPopover } from './ShiftPopover';
import { SelectionPopover } from './SelectionPopover';
import { ViewSettingsPopover } from './ViewSettingsPopover';
import { ToolbarTooltip } from './ToolbarTooltip';
import { useToolbarResize } from './useToolbarResize';
import { toolbarStore, type ToolbarStage } from './toolbarStore';
import {
  btnBase,
  dividerStyle,
  loadToolbarPosition,
  saveToolbarPosition,
  clearToolbarPosition,
} from './toolbarStyles';

export interface BoardV3FloatingToolbarProps {
  onAddTask: () => void;
}

function scheduleAfter(action: () => void) {
  action();
  setTimeout(() => recalculateSchedule(), 0);
}

// ── Adaptive sizes per stage ──
const STAGE_CONFIG: Record<ToolbarStage, {
  btnSize: number;
  iconSize: number;
  gap: number;
  px: number;
  dividerH: number;
  borderRadius: number;
  gripSize: number;
}> = {
  tiny:   { btnSize: 10, iconSize: 6,  gap: 2, px: 4,  dividerH: 8,  borderRadius: 6,  gripSize: 6  },
  small:  { btnSize: 18, iconSize: 10, gap: 3, px: 6,  dividerH: 14, borderRadius: 8,  gripSize: 8  },
  medium: { btnSize: 24, iconSize: 12, gap: 4, px: 8,  dividerH: 18, borderRadius: 10, gripSize: 10 },
  full:   { btnSize: 32, iconSize: 16, gap: 6, px: 10, dividerH: 24, borderRadius: 12, gripSize: 14 },
};

export const BoardV3FloatingToolbar = memo(function BoardV3FloatingToolbar({
  onAddTask,
}: BoardV3FloatingToolbarProps) {
  // ── i18n ──
  const { t } = useTranslation();

  // ── Store access ──
  const selectedIds = useSelector(selectionStore, s => s.context.selectedIds);
  const selectedCount = selectedIds.length;
  const hasSelection = selectedCount > 0;

  const tasks = useAllTasks();
  const { shiftDays, removeFromBoard, deleteTask } = useRoadmapActions();
  const boardStartDate = useBoardStartDate();

  // ── Toolbar resize (4-stage, height) ──
  const {
    toolbarHeight, stage, isResizing, resizeDirectionRef,
    handleResizeStartTop, handleResizeStartBottom, handleResizeDoubleClick,
  } = useToolbarResize();
  const cfg = STAGE_CONFIG[stage];

  // Adaptive button style
  const adaptBtn = useMemo((): React.CSSProperties => ({
    ...btnBase,
    height: cfg.btnSize,
    width: cfg.btnSize,
    borderRadius: Math.max(3, cfg.btnSize / 6),
  }), [cfg.btnSize]);

  // Adaptive divider
  const adaptDivider = useMemo((): React.CSSProperties => ({
    ...dividerStyle,
    height: cfg.dividerH,
    margin: `0 ${Math.max(1, cfg.gap / 2)}px`,
  }), [cfg.dividerH, cfg.gap]);

  // ── Undo stack ──
  const [undoStack, setUndoStack] = useState<Array<Map<string, string>>>([]);
  const canUndo = undoStack.length > 0;

  const snapshotStartDates = useCallback(() => {
    const ctx = globalTasksStore.getSnapshot().context;
    const snapshot = new Map<string, string>();
    for (const id of ctx.taskIds) {
      const t = ctx.tasksById[id];
      if (t) snapshot.set(id, t.startDate);
    }
    setUndoStack(prev => [...prev, snapshot]);
  }, []);

  const handleUndo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const snapshot = prev[prev.length - 1];
      for (const [taskId, startDate] of snapshot) {
        globalTasksStore.send({ type: 'task.updateStartDate', taskId, startDate });
      }
      setTimeout(() => recalculateSchedule(), 0);
      return prev.slice(0, -1);
    });
  }, []);

  // ── Shift logic ──
  const handleShift = useCallback((delta: number) => {
    snapshotStartDates();
    const ids = selectionStore.getSnapshot().context.selectedIds;
    if (ids.length > 0) {
      scheduleAfter(() => shiftDays(delta, ids));
    } else {
      scheduleAfter(() => shiftDays(delta));
    }
  }, [shiftDays, snapshotStartDates]);

  const handleMoveToDate = useCallback((targetDate: string) => {
    const ids = selectionStore.getSnapshot().context.selectedIds;
    const relevantTasks = ids.length > 0
      ? tasks.filter(t => ids.includes(t.id))
      : tasks;
    if (relevantTasks.length === 0) return;
    let earliestDayIndex = Infinity;
    for (const t of relevantTasks) {
      const di = getTaskDayIndex(t.startDate, boardStartDate);
      if (di < earliestDayIndex) earliestDayIndex = di;
    }
    const targetDayIndex = dateToBusinessDayIndex(targetDate, boardStartDate);
    const delta = targetDayIndex - earliestDayIndex;
    if (delta === 0) return;
    snapshotStartDates();
    if (ids.length > 0) {
      scheduleAfter(() => shiftDays(delta, ids));
    } else {
      scheduleAfter(() => shiftDays(delta));
    }
  }, [tasks, boardStartDate, shiftDays, snapshotStartDates]);

  // ── Selection actions ──
  const handleRemoveSelectedFromBoard = useCallback(() => {
    const ids = selectionStore.getSnapshot().context.selectedIds;
    for (const id of ids) removeFromBoard(id);
    selectionStore.send({ type: 'selection.clear' });
    setTimeout(() => recalculateSchedule(), 0);
  }, [removeFromBoard]);

  const handleDeleteSelected = useCallback(() => {
    const ids = selectionStore.getSnapshot().context.selectedIds;
    for (const id of ids) deleteTask(id);
    selectionStore.send({ type: 'selection.clear' });
    setTimeout(() => recalculateSchedule(), 0);
  }, [deleteTask]);

  // ── TaskPool (unscoped) ──
  const handleOpenTaskPool = useCallback(() => {
    popupStore.send({ type: 'popup.requestTaskPool' });
  }, []);

  // ── Popover state ──
  const [showShiftPopover, setShowShiftPopover] = useState(false);
  const [showSelectionPopover, setShowSelectionPopover] = useState(false);
  const [showViewSettings, setShowViewSettings] = useState(false);
  const shiftTriggerRef = useRef<HTMLButtonElement>(null);
  const badgeTriggerRef = useRef<HTMLButtonElement>(null);
  const viewSettingsTriggerRef = useRef<HTMLButtonElement>(null);

  const handleToggleShiftPopover = useCallback(() => {
    setShowShiftPopover(prev => !prev);
    setShowSelectionPopover(false);
    setShowViewSettings(false);
  }, []);
  const handleCloseShiftPopover = useCallback(() => setShowShiftPopover(false), []);

  const handleToggleSelectionPopover = useCallback(() => {
    setShowSelectionPopover(prev => !prev);
    setShowShiftPopover(false);
    setShowViewSettings(false);
  }, []);
  const handleCloseSelectionPopover = useCallback(() => setShowSelectionPopover(false), []);

  const handleToggleViewSettings = useCallback(() => {
    setShowViewSettings(prev => !prev);
    setShowShiftPopover(false);
    setShowSelectionPopover(false);
  }, []);
  const handleCloseViewSettings = useCallback(() => setShowViewSettings(false), []);

  // ── Position drag state ──
  const toolbarRef = useRef<HTMLElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(() => loadToolbarPosition());
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Track bottom edge during top-resize so top follows mouse, bottom stays fixed.
  // For bottom-resize, top stays fixed (no adjustment needed).
  const resizeBottomEdgeRef = useRef(0);
  const prevHeightRef = useRef(toolbarHeight);

  useEffect(() => {
    if (isResizing && position && resizeDirectionRef.current === 1) {
      if (prevHeightRef.current !== toolbarHeight) {
        setPosition(prev => {
          if (!prev) return prev;
          const newY = resizeBottomEdgeRef.current - toolbarHeight;
          return { x: prev.x, y: Math.max(0, newY) };
        });
      }
    }
    prevHeightRef.current = toolbarHeight;
  }, [isResizing, toolbarHeight, position, resizeDirectionRef]);

  // Wrap top-resize start to snapshot the bottom edge
  const handleResizeStartTopWrapped = useCallback((e: React.MouseEvent) => {
    if (position) {
      resizeBottomEdgeRef.current = position.y + toolbarHeight;
    }
    handleResizeStartTop(e);
  }, [position, toolbarHeight, handleResizeStartTop]);

  const clampPosition = useCallback((x: number, y: number) => {
    const el = toolbarRef.current;
    if (!el) return { x, y };
    const rect = el.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(x, window.innerWidth - rect.width)),
      y: Math.max(0, Math.min(y, window.innerHeight - rect.height)),
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      setPosition(prev => prev ? clampPosition(prev.x, prev.y) : null);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampPosition]);

  const handleGripMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const el = toolbarRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    isDragging.current = true;
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    function onMouseMove(ev: MouseEvent) {
      if (!isDragging.current) return;
      setPosition(clampPosition(
        ev.clientX - dragOffset.current.x,
        ev.clientY - dragOffset.current.y,
      ));
    }
    function onMouseUp() {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setPosition(prev => { if (prev) saveToolbarPosition(prev.x, prev.y); return prev; });
    }
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [clampPosition]);

  const handleGripDoubleClick = useCallback(() => {
    setPosition(null);
    clearToolbarPosition();
  }, []);

  // ── Toolbar style ──
  const baseStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 9998,
    display: 'flex',
    alignItems: 'center',
    gap: cfg.gap,
    height: toolbarHeight,
    padding: `0 ${cfg.px}px`,
    backgroundColor: '#fff',
    borderRadius: cfg.borderRadius,
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    border: '1px solid #e5e7eb',
    userSelect: 'none' as const,
  };

  const toolbarStyle: React.CSSProperties = position
    ? { ...baseStyle, left: position.x, top: position.y }
    : { ...baseStyle, bottom: 24, left: '50%', transform: 'translateX(-50%)' };

  // ── Badge size adapts to stage ──
  const badgeSize = Math.max(12, cfg.btnSize * 0.75);

  // Expand from tiny → medium (second-to-last stage)
  const handleExpandFromTiny = useCallback(() => {
    toolbarStore.send({ type: 'toolbar.setStage', stage: 'medium' });
  }, []);

  // ── Tiny: grip handle + ^ expand button ──
  if (stage === 'tiny') {
    const tinyStyle: React.CSSProperties = position
      ? { position: 'fixed', left: position.x, top: position.y, zIndex: 9998 }
      : { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9998 };

    return (
      <div
        ref={toolbarRef as React.RefObject<HTMLDivElement | null>}
        style={{
          ...tinyStyle,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height: 14,
          padding: '0 3px',
          border: '1px solid #e5e7eb',
          borderRadius: 7,
          backgroundColor: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
          userSelect: 'none' as const,
        }}
      >
        <div
          onMouseDown={handleGripMouseDown}
          onDoubleClick={handleGripDoubleClick}
          style={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            color: '#9ca3af',
          }}
          title={t('boardv3.toolbar.moveToolbar')}
        >
          <GripVertical size={6} />
        </div>
        <button
          onClick={handleExpandFromTiny}
          style={{
            padding: 0,
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: '#9ca3af',
          }}
          title={t('boardv3.toolbar.openToolbar')}
        >
          <ChevronUp size={8} />
        </button>
      </div>
    );
  }

  return (
    <div ref={toolbarRef as React.RefObject<HTMLDivElement | null>} style={toolbarStyle}>
      {/* ── Resize handle (top edge) ── */}
      <div
        onMouseDown={handleResizeStartTopWrapped}
        onDoubleClick={handleResizeDoubleClick}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: 5,
          cursor: 'row-resize',
          backgroundColor: isResizing && resizeDirectionRef.current === 1 ? '#3b82f6' : 'transparent',
          borderRadius: `${cfg.borderRadius}px ${cfg.borderRadius}px 0 0`,
          transition: isResizing ? 'none' : 'background-color 0.15s',
        }}
      />

      {/* ── Resize handle (bottom edge) ── */}
      <div
        onMouseDown={handleResizeStartBottom}
        onDoubleClick={handleResizeDoubleClick}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 5,
          cursor: 'row-resize',
          backgroundColor: isResizing && resizeDirectionRef.current === -1 ? '#3b82f6' : 'transparent',
          borderRadius: `0 0 ${cfg.borderRadius}px ${cfg.borderRadius}px`,
          transition: isResizing ? 'none' : 'background-color 0.15s',
        }}
      />

      {/* ── Grip handle ── */}
      <div
        onMouseDown={handleGripMouseDown}
        onDoubleClick={handleGripDoubleClick}
        style={{
          cursor: isDragging.current ? 'grabbing' : 'grab',
          display: 'flex',
          alignItems: 'center',
          padding: '0 1px',
          color: '#9ca3af',
          flexShrink: 0,
        }}
        title={t('boardv3.toolbar.moveToolbar')}
      >
        <GripVertical size={cfg.gripSize} />
      </div>

      <div style={adaptDivider} />

      {/* ── Shift button ── */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <ToolbarTooltip label={t('boardv3.toolbar.shiftTasks')}>
          <button
            ref={shiftTriggerRef}
            onClick={handleToggleShiftPopover}
            style={{
              ...adaptBtn,
              backgroundColor: showShiftPopover ? '#eff6ff' : '#f9fafb',
              borderColor: showShiftPopover ? '#93c5fd' : '#e5e7eb',
            }}
          >
            <ArrowLeftRight size={cfg.iconSize} />
          </button>
        </ToolbarTooltip>

        {showShiftPopover && (
          <ShiftPopover
            onShift={handleShift}
            onMoveToDate={handleMoveToDate}
            onClose={handleCloseShiftPopover}
            hasSelection={hasSelection}
            triggerRef={shiftTriggerRef}
          />
        )}
      </div>

      {/* ── Undo button ── */}
      {canUndo && (
        <ToolbarTooltip label={t('boardv3.toolbar.undo')}>
          <button onClick={handleUndo} style={adaptBtn}>
            <Undo2 size={cfg.iconSize} />
          </button>
        </ToolbarTooltip>
      )}

      {/* ── View settings (eye) ── */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <ToolbarTooltip label={t('boardv3.toolbar.view')}>
          <button
            ref={viewSettingsTriggerRef}
            onClick={handleToggleViewSettings}
            style={{
              ...adaptBtn,
              backgroundColor: showViewSettings ? '#eff6ff' : '#f9fafb',
              borderColor: showViewSettings ? '#93c5fd' : '#e5e7eb',
            }}
          >
            <Eye size={cfg.iconSize} />
          </button>
        </ToolbarTooltip>

        {showViewSettings && (
          <ViewSettingsPopover
            onClose={handleCloseViewSettings}
            triggerRef={viewSettingsTriggerRef}
          />
        )}
      </div>

      <div style={adaptDivider} />

      {/* ── Add task ── */}
      <ToolbarTooltip label={t('boardv3.toolbar.addNewTask')}>
        <button onClick={onAddTask} style={adaptBtn}>
          <Plus size={cfg.iconSize} />
        </button>
      </ToolbarTooltip>

      {/* ── Task Pool ── */}
      <ToolbarTooltip label={t('boardv3.toolbar.openTaskPool')}>
        <button onClick={handleOpenTaskPool} style={adaptBtn}>
          <ClipboardList size={cfg.iconSize} />
        </button>
      </ToolbarTooltip>

      {/* ── Selection-dependent buttons ── */}
      {selectedCount > 0 && (
        <>
          <div style={adaptDivider} />
          <ToolbarTooltip label={t('boardv3.toolbar.removeSelectedFromBoard')}>
            <button onClick={handleRemoveSelectedFromBoard} style={adaptBtn}>
              <MinusCircle size={cfg.iconSize} />
            </button>
          </ToolbarTooltip>
          <ToolbarTooltip label={t('boardv3.toolbar.deleteSelected')}>
            <button onClick={handleDeleteSelected} style={adaptBtn}>
              <Trash2 size={cfg.iconSize} />
            </button>
          </ToolbarTooltip>
        </>
      )}

      {/* ── Selection badge (absolute top-right) ── */}
      {selectedCount > 0 && (
        <div style={{ position: 'relative' }}>
          <button
            ref={badgeTriggerRef}
            onClick={handleToggleSelectionPopover}
            style={{
              position: 'absolute',
              top: -(badgeSize * 1.2),
              right: -(badgeSize * 0.8),
              height: badgeSize,
              minWidth: badgeSize,
              padding: '0 3px',
              fontSize: Math.max(7, badgeSize * 0.55),
              fontWeight: 700,
              border: 'none',
              borderRadius: badgeSize / 2,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#3b82f6',
              color: '#fff',
              lineHeight: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          >
            {selectedCount}
          </button>

          {showSelectionPopover && (
            <SelectionPopover
              onClose={handleCloseSelectionPopover}
              triggerRef={badgeTriggerRef}
            />
          )}
        </div>
      )}
    </div>
  );
});
